import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "suggestions"]
  static values = { 
    searchUrl: String,
    postId: String
  }
  
  connect() {
    this.currentMentionStart = -1
    this.currentMentionText = ""
    this.selectedIndex = -1
  }
  
  handleKeyup(event) {
    const input = this.inputTarget
    const cursorPosition = input.selectionStart
    const text = input.value
    
    // Find the @ symbol before cursor
    let mentionStart = -1
    for (let i = cursorPosition - 1; i >= 0; i--) {
      if (text[i] === '@') {
        // Check if this @ is at the start or preceded by whitespace
        if (i === 0 || /\s/.test(text[i - 1])) {
          mentionStart = i
          break
        }
      } else if (/\s/.test(text[i])) {
        // Hit whitespace without finding @, stop looking
        break
      }
    }
    
    if (mentionStart >= 0) {
      const mentionText = text.substring(mentionStart + 1, cursorPosition)
      
      // Only show suggestions if we have at least 1 character after @
      if (mentionText.length >= 1 && /^\w*$/.test(mentionText)) {
        this.currentMentionStart = mentionStart
        this.currentMentionText = mentionText
        this.searchUsers(mentionText)
      } else {
        this.hideSuggestions()
      }
    } else {
      this.hideSuggestions()
    }
    
    // Handle arrow keys for navigation only when suggestions are visible
    if (!this.suggestionsTarget.classList.contains('hidden')) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.navigateDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.navigateUp()
      } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
        event.preventDefault()
        this.selectUser()
      } else if (event.key === 'Escape') {
        this.hideSuggestions()
      }
    }
  }
  
  async searchUsers(query) {
    if (query.length < 1) {
      this.hideSuggestions()
      return
    }
    
    try {
      const response = await fetch(`/users/search?q=${encodeURIComponent(query)}&limit=4`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })
      
      if (response.ok) {
        const users = await response.json()
        this.showSuggestions(users)
      }
    } catch (error) {
      console.error('Error searching users:', error)
    }
  }
  
  showSuggestions(users) {
    if (users.length === 0) {
      this.hideSuggestions()
      return
    }
    
    this.selectedIndex = -1
    
    const suggestionsHtml = users.map((user, index) => {
      const username = this.escapeHtml(user.username)
      const displayName = this.escapeHtml(user.display_name)
      const avatarUrl = this.safeUrl(user.avatar_url)
      const initial = this.escapeHtml((user.display_name || "").charAt(0).toUpperCase())
      return `
      <div class="mention-suggestion px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-night-primary/10 flex items-center space-x-2 ${index === this.selectedIndex ? 'bg-blue-50 dark:bg-night-primary/10' : ''}"
           data-action="click->mention#selectUser"
           data-mention-username-param="${username}"
           data-mention-display-name-param="${displayName}">
        ${avatarUrl ?
          `<img src="${avatarUrl}" class="w-6 h-6 rounded-full object-cover" alt="${displayName}">` :
          `<div class="w-6 h-6 rounded-full bg-blue-400 dark:bg-night-primary flex items-center justify-center text-white font-bold text-xs">
            ${initial}
          </div>`
        }
        <div>
          <div class="text-sm font-medium text-gray-900 dark:text-night-text-primary">@${username}</div>
          <div class="text-xs text-gray-500 dark:text-night-text-muted">${displayName}</div>
        </div>
      </div>
    `}).join('')
    
    this.suggestionsTarget.innerHTML = suggestionsHtml
    this.suggestionsTarget.classList.remove('hidden')
    this.positionSuggestions()
  }
  
  hideSuggestions() {
    this.suggestionsTarget.classList.add('hidden')
    this.selectedIndex = -1
  }
  
  positionSuggestions() {
    const input = this.inputTarget
    const inputRect = input.getBoundingClientRect()
    const suggestions = this.suggestionsTarget
    
    // Use fixed positioning to position relative to the viewport
    suggestions.style.position = 'fixed'
    suggestions.style.top = `${inputRect.bottom + 4}px` // A bit more space below the input
    suggestions.style.left = `${inputRect.left}px`
    suggestions.style.width = `${Math.min(inputRect.width, 300)}px`
    suggestions.style.zIndex = '9999' // Higher z-index to ensure it's above everything
    suggestions.style.maxHeight = '200px'
    suggestions.style.overflowY = 'auto'
    
    // Ensure the dropdown is properly visible
    suggestions.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }
  
  navigateDown() {
    const suggestions = this.suggestionsTarget.querySelectorAll('.mention-suggestion')
    if (suggestions.length === 0) return
    
    this.selectedIndex = Math.min(this.selectedIndex + 1, suggestions.length - 1)
    this.updateSelection()
  }
  
  navigateUp() {
    const suggestions = this.suggestionsTarget.querySelectorAll('.mention-suggestion')
    if (suggestions.length === 0) return
    
    this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
    this.updateSelection()
  }
  
  updateSelection() {
    const suggestions = this.suggestionsTarget.querySelectorAll('.mention-suggestion')
    suggestions.forEach((suggestion, index) => {
      if (index === this.selectedIndex) {
        suggestion.classList.add('bg-blue-50', 'dark:bg-night-primary/10')
      } else {
        suggestion.classList.remove('bg-blue-50', 'dark:bg-night-primary/10')
      }
    })
  }
  
  selectUser(event) {
    const suggestion = event?.params ? event.currentTarget : 
                     this.suggestionsTarget.querySelectorAll('.mention-suggestion')[this.selectedIndex]
    
    if (!suggestion) return
    
    const username = event?.params?.username || 
                    suggestion.dataset.mentionUsernameParam
    
    if (!username || this.currentMentionStart === -1) return
    
    const input = this.inputTarget
    const text = input.value
    const beforeMention = text.substring(0, this.currentMentionStart)
    const afterMention = text.substring(input.selectionStart)
    
    const newText = beforeMention + `@${username} ` + afterMention
    const newCursorPosition = this.currentMentionStart + username.length + 2 // +2 for @ and space
    
    input.value = newText
    input.setSelectionRange(newCursorPosition, newCursorPosition)
    input.focus()
    
    this.hideSuggestions()
    this.currentMentionStart = -1
    this.currentMentionText = ""
  }

  escapeHtml(text) {
    // Escape quotes too — these values land inside double-quoted attributes
    // (alt=, data-*-param=), where a bare " would break out of the attribute.
    return String(text == null ? "" : text).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  // Allow only safe http(s)/relative URLs for avatar src; reject javascript: etc.
  safeUrl(url) {
    if (!url) return ""
    const raw = String(url).trim()
    if (/^(https?:\/\/|\/)/i.test(raw)) {
      return raw.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E")
    }
    return ""
  }
}